import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import Button from "./Button";
import { useTranslationContext } from "../../Hooks/useTranslationContext";
import example_eng from "../../assets/example_eng.png";
import example_swe from "../../assets/example_swe.png";
import { StyledInput } from "./Inputs";
import { getNameErrorKey } from "Helpers/validation";

const popupAnimation = keyframes`
  0%{
    transform: scale(0.4);
  }
  50%{
    transform: scale(1.1);
  }
  100%{
    transform: scale(1);
  }
}`;

const StyledDiv = styled.div`
  padding: 0.8em;
  margin: 0.8em;
  overflow: auto;
`;

const PopUpContainer = styled.div`
  z-index: 1;
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  background-color: var(--color-bg-highlight);
`;

const InnerPopup = styled.div`
  animation: ${popupAnimation} 0.3s linear forwards;
  text-align: center;
  color: var(--color-text-light);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 2em;
  right: 2em;
  top: 1vw;
  bottom: 1vw;
  max-width: 30em;
  max-height: 26em;
  margin: auto;
  border-radius: 1em;
  background: var(--color-bg-primary);
  z-index: 100;

  input {
    background: var(--color-text-light);
    color: var(--color-text-dark);
    border-radius: 0.4em;
    font-family: inherit;
    font-weight: bold;
  }
`;

const ErrorMessage = styled.div`
  padding: 0.8em;
  position: absolute;
`;

interface PopupProps {
  onOverlayClick?: () => void;
}

export const withPopup =
  <P extends object>(
    WrappedComponent: React.ComponentType<P>
  ): React.FC<P & PopupProps> =>
  ({ onOverlayClick, ...props }: PopupProps) =>
    (
      <PopUpContainer onClick={onOverlayClick}>
        <InnerPopup onClick={(e) => e.stopPropagation()}>
          <WrappedComponent {...(props as P)} />
        </InnerPopup>
      </PopUpContainer>
    );

interface NamePromptProps {
  onSubmit: (name: string) => void;
  lastUsedName: string;
  names: string[];
}

const NamePromptContent: React.FunctionComponent<NamePromptProps> = ({
  onSubmit,
  lastUsedName,
  names,
}: NamePromptProps) => {
  const [name, setName] = useState(lastUsedName);
  const { t } = useTranslationContext();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const handleOnSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errorKey = getNameErrorKey(name, names);
    if (errorKey) {
      setErrorMessage(t[errorKey]);
    } else {
      onSubmit(name);
    }
  };
  return (
    <div>
      <h1>{t["whats.your.name"]}</h1>
      <form onSubmit={handleOnSubmit}>
        <StyledDiv>
          <StyledInput
            value={name}
            onChange={(v) => setName(v.target.value.substring(0, 25))}
          />
          <Button autoFocus type="submit">
            {t["submit"]}
          </Button>
        </StyledDiv>
      </form>
      <ErrorMessage>{errorMessage}</ErrorMessage>
    </div>
  );
};

const StyledImg = styled.img`
  width: 100%;
  max-width: 15em;
`;
const StyledUl = styled.ul`
  text-align: left;
`;
interface InstructionsContentProps {
  onClose: () => void;
}
const InstructionsContent = ({ onClose }: InstructionsContentProps) => {
  const { t, language } = useTranslationContext();
  const r = useRef<HTMLDivElement>(null);
  useEffect(() => {
    r.current?.scrollTo(0, 0);
  }, [onClose]);
  return (
    <StyledDiv ref={r}>
      <h1>{t["how.to.play"]}</h1>
      <p>{t["instructions"]}</p>
      <StyledUl>
        <li>{t["category.example.1"]}</li>
        <li>{t["category.example.2"]}</li>
        <li>{t["category.example.3"]}</li>
        <li>{t["category.example.4"]}</li>
      </StyledUl>
      <p>{t["instructions.cont"]}</p>
      <h2>{t["scoring"]}</h2>
      <p>{t["scoring.instructions"]}</p>
      <p>{t["scoring.example"]}</p>
      <StyledImg src={language === "eng" ? example_eng : example_swe} />
      <p>{t["scoring.example.explanation"]}</p>
      <StyledDiv>
        <Button autoFocus onClick={onClose}>
          {t["close"]}
        </Button>
      </StyledDiv>
    </StyledDiv>
  );
};

interface ConfirmPopupProps {
  onSubmit: () => void;
  onCancel: () => void;
  message: string;
}

const ConfirmPopupContent = ({
  onSubmit,
  onCancel,
  message,
}: ConfirmPopupProps) => {
  const handleOnSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };
  return (
    <>
      <h1>{message} </h1>
      <form onSubmit={handleOnSubmit}>
        <Button animated={true} onClick={onCancel}>
          NO
        </Button>
        <Button type="submit" isSecondary animated={false}>
          YES
        </Button>
      </form>
    </>
  );
};

interface ConfirmPopupProps {
  onSubmit: () => void;
  onCancel: () => void;
}

const DisconnectedPlayerPopupContent = () => {
  const { t } = useTranslationContext();
  return (
    <>
      <h1>{t["you.disconnected"]}</h1>
      <p>{t["please.reload.page"]}</p>
    </>
  );
};

export const NamePrompt = withPopup(NamePromptContent);
export const InstructionsPopup = withPopup(InstructionsContent);
export const ConfirmPopup = withPopup(ConfirmPopupContent);
export const DisconnectedPlayerPopup = withPopup(
  DisconnectedPlayerPopupContent
);
